import React, { useEffect, useState, useContext, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FaShareAlt, FaHeart, FaRegHeart } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import config from "../../config";

import Navbar from "../../components/Navbar/Navbar";
import Footer from "../../components/Footer/Footer";
import KitchenImages from "./components/KitchenImages";
import KitchenGallery from "./components/KitchenGallery";
import KitchenDescription from "./components/KitchenDescription";
import KitchenPricing from "./components/KitchenPricing";
import KitchenAmenities from "./components/KitchenAmenities";
import KitchenAvailability from "./components/KitchenAvailability";
import KitchenBooking from "./components/KitchenBooking";
import ShareButton from "../../components/ShareButton/ShareButton";
import AuthModal from "../../components/modals/AuthModal/AuthModal";
import { AuthContext } from "../../context/AuthContext";
import { useKitchen } from "../../context/KitchenContext";
import BookNowBar from "./components/BookNowBar";

const Kitchen = () => {
  const { kitchenID } = useParams();
  const [kitchen, setKitchen] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user, token, favourites, setFavourites } = useContext(AuthContext);
  const { fetchKitchenById } = useKitchen();

  // Gallery State
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);
  const [isFavourite, setIsFavourite] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [animation, setAnimation] = useState("");

  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login");

  const [showBookNowBar, setShowBookNowBar] = useState(false);
  const bookingSectionRef = useRef(null);

  const [checkinDate, setCheckinDate] = useState(null);
  const [checkoutDate, setCheckoutDate] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState({
    message: "",
    type: "",
    suggestion: null,
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md")); // Detect mobile view

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await fetchKitchenById(kitchenID);
        setKitchen(data);
      } catch (err) {
        toast.error("Kitchen not found", {
          position: "top-right",
          autoClose: 2000,
        });
      } finally {
        setLoading(false);
      }
    };
    if (kitchenID) fetch();
  }, [kitchenID]);

  // This useEffect handles the visibility of the BookNowBar based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (bookingSectionRef.current) {
        const bookingRect = bookingSectionRef.current.getBoundingClientRect();
        // Show the bar only when the booking section is not visible in the viewport
        setShowBookNowBar(bookingRect.top > window.innerHeight || bookingRect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check on load

    return () => window.removeEventListener('scroll', handleScroll);
  }, []); // Empty dependency array ensures this runs only on mount and unmount

  useEffect(() => {
    if (favourites && kitchenID) {
      setIsFavourite(favourites.includes(kitchenID));
    }
  }, [favourites, kitchenID]);

  const openAuthModal = (mode = "login") => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };
  const closeAuthModal = () => {
    setShowAuthModal(false);
  };

  const handleFavouriteToggle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (!user) {
      openAuthModal("login");
      return;
    }

    const previousFavouriteState = isFavourite;
    setIsFavourite(!isFavourite);
    setAnimation(isFavourite ? "animate-shake" : "animate-pop");
    setTimeout(() => setAnimation(""), 300);

    try {
      if (previousFavouriteState) {
        await axios.post(
          `${config.apiBaseUrl}/api/auth/unfavourite`,
          { userID: user.userID, kitchenID },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFavourites((prev) => prev.filter((id) => id !== kitchenID));
        toast.info("Removed from favourites");
      } else {
        await axios.post(
          `${config.apiBaseUrl}/api/auth/favourites`,
          { userID: user.userID, kitchenID },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setFavourites((prev) => [...prev, kitchenID]);
        toast.success("Added to favourites!", {
          icon: "💖",
          position: "top-right",
        });
      }
    } catch (error) {
      console.error(
        "Error toggling favourite:",
        error.response?.data || error.message
      );
      setIsFavourite(previousFavouriteState);
      setFavourites((prev) =>
        previousFavouriteState
          ? [...prev, kitchenID].filter(
              (id, idx, self) => self.indexOf(id) === idx
            )
          : prev.filter((id) => id !== kitchenID)
      );
    }
  };

  const openGallery = (index) => {
    setGalleryStartIndex(index);
    setGalleryOpen(true);
  };

  // This function now also hides the bar on click
  const scrollToBooking = () => {
    setShowBookNowBar(false); // Hide the bar
    bookingSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
          className="loading-container"
        >
          <CircularProgress size={60} />
        </Box>
        <Footer />
        <ToastContainer position="top-right" autoClose={2000} />
      </>
    );
  }
  if (!kitchen) {
    return (
      <>
        <Navbar />
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
          }}
        >
          <Typography variant="h6">Kitchen not found.</Typography>
        </Box>
        <Footer />
        <ToastContainer position="top-right" autoClose={2000} />
      </>
    );
  }

  return (
    <>
      <BookNowBar
        totalAmount={totalAmount}
        checkinDate={checkinDate}
        checkoutDate={checkoutDate}
        isCheckingAvailability={isCheckingAvailability}
        onBookNowClick={scrollToBooking}
        show={showBookNowBar}
        kitchen={kitchen}
      />
      <Navbar />
      {isMobile && (
        <KitchenImages 
          kitchen={kitchen}
          onImageClick={openGallery}
          isFavourite={isFavourite}
          onFavouriteToggle={handleFavouriteToggle}
          onShareClick={() => setShowSharePopup(true)}
        />
        )}
      <Container maxWidth="lg" sx={{ pt: 2, pb: 4, pl: 2 }}>
        {/* Desktop Header */}
        {!isMobile && (
          <Box
            sx={{
              display: { xs: "none", md: "flex" },
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2, // Margin below header
              px: 0,
            }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              color="#1a1a1a"
              sx={{ mr: 2 }}
            >
              {kitchen.Title || "Untitled Kitchen"}
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton
                onClick={() => setShowSharePopup(true)}
                sx={{
                  color: "#666",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    color: "#1a73e8",
                  },
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  p: 1,
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                }}
              >
                <FaShareAlt />
              </IconButton>
              <IconButton
                onClick={handleFavouriteToggle}
                sx={{
                  color: isFavourite ? "#e63946" : "#666",
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 1)",
                    color: isFavourite ? "#d32f2f" : "#1a73e8",
                  },
                  borderRadius: "50%",
                  width: 40,
                  height: 40,
                  p: 1,
                  boxShadow: "0 2px 6px rgba(0, 0, 0, 0.1)",
                  transition: "all 0.3s ease",
                  "&.animate-pop": {
                    animation: "pop 0.3s ease-out",
                  },
                  "&.animate-shake": {
                    animation: "shake 0.3s ease-out",
                  },
                }}
                className={animation}
              >
                {isFavourite ? <FaHeart /> : <FaRegHeart />}
              </IconButton>
            </Stack>
          </Box>
        )}

        {/* Kitchen Images */}
        {!isMobile && (
        <KitchenImages maxWidth="lg" sx={{ pt: 2, pb: 4, pl: 2 }}
          kitchen={kitchen}
          onImageClick={openGallery}
          isFavourite={isFavourite}
          onFavouriteToggle={handleFavouriteToggle}
          onShareClick={() => setShowSharePopup(true)}
        />
        )}

        {/* Title and Tags: Below images for mobile, adjusted for desktop */}
        <Box
          sx={{
            mt: { xs: 2, md: 0 },
            textAlign: { xs: "center", md: "left" },
            px: { xs: 2, md: 0 },
            display: { md: "none" },
          }}
        >
          {isMobile && (
            <>
              <Typography
                variant="h4"
                color="#1a1a1a"
              >
                {kitchen.Title || "Untitled Kitchen"}
              </Typography>
            </>
          )}
        </Box>

        <ShareButton
          link={window.location.href}
          forceOpen={showSharePopup}
          onClose={() => setShowSharePopup(false)}
        />

        <KitchenGallery
          open={galleryOpen}
          onClose={() => setGalleryOpen(false)}
          images={kitchen.Images || []}
          startIndex={galleryStartIndex}
        />

        <Grid container spacing={2} mt={0}>
          <Grid item xs={12} md={8} sx={{ pt: 0, pl: 0 }}>
            <Box display="flex" flexDirection="column" gap={2}>
              <KitchenDescription kitchen={kitchen} />
              <KitchenPricing kitchen={kitchen} />
              <KitchenAmenities kitchen={kitchen} />
              <KitchenAvailability kitchen={kitchen} />
            </Box>
          </Grid>

          <Grid item xs={12} md={4} sx={{ pt: 0 }}>
            <Box ref={bookingSectionRef} sx={{ position: "sticky", top: 120, alignSelf: "flex-start" }}>
              <KitchenBooking
                kitchen={kitchen}
                kitchenID={kitchenID}
                checkinDate={checkinDate}
                setCheckinDate={setCheckinDate}
                checkoutDate={checkoutDate}
                setCheckoutDate={setCheckoutDate}
                totalAmount={totalAmount}
                setTotalAmount={setTotalAmount}
                isCheckingAvailability={isCheckingAvailability}
                setIsCheckingAvailability={setIsCheckingAvailability}
                availabilityStatus={availabilityStatus}
                setAvailabilityStatus={setAvailabilityStatus}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
      <Footer />
      <ToastContainer position="top-right" autoClose={2000} />
      <AuthModal
        open={showAuthModal}
        onClose={closeAuthModal}
        initialMode={authMode}
      />
    </>
  );
};

export default Kitchen;
